import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _last from 'lodash/last';
import _identity from 'lodash/identity';
import { ExportIcon, ContentCurrentIcon, InventoryIcon, CompareScaleIcon } from 'src/components/SvgIcons';
import PageContextMenu from 'src/components/PageContextMenu';
import getSummaryPageLayout from './SummaryPageLayout';
import { flattenWidgets } from 'src/components/Layout/LayoutUtil';
import SubtabHeader from 'src/components/GlobalComponents/SubtabHeader';
import AtlasV2SubtabNav from 'src/components/AtlasV2Components/AtlasV2LayoutComponents/Header/AtlasV2SubtabNav';
import getAtlasV2KeyMetricsLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2KeyMetricsLayout';
import getAtlasV2TrafficsPageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2TrafficsPageLayout';
import getAtlasV2PricePageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2PricePageLayout';
import getAtlasV2ContentPageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2ContentPageLayout';
import getAtlasV2RatingPageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2RatingPageLayout';
import getAtlasV2SalesPageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2SalesPageLayout';
import getAtlasV2ConversionLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasV2ConversionLayout';
import getAtlasTrafficsV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasTrafficsV2PageLayout';
import getAtlasPriceV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasPriceV2PageLayout';
import getAtlasConversionV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasConversionV2PageLayout';
import getAtlasSalesV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasSalesV2PageLayout';
import getAtlasContentV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasContentV2PageLayout';
import getAtlasRatingsV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtlasRatingsV2PageLayout';
import getAtlasV2KeyMetricsV2PageLayout from 'src/components/AtlasV2Components/AtlasV2PageLayouts/getAtlasV2Layouts/getAtalsV2KeyMetricsV2Layout';

export const HIDE_GROUP_BY_LEVEL: Record<string, Record<string, boolean>> = {
  subcategory: {
    categoryId: true,
    subCategoryId: true
  },
  brand: {
    parentBrandId: true
  }
};

const getPageLayoutGetter = (name: string) => {
  return (
    {
      summaryPage: getSummaryPageLayout,
      keymetrics: getAtlasV2KeyMetricsLayout,
      keyMetricsV2: getAtlasV2KeyMetricsV2PageLayout,
      all: getAtlasV2KeyMetricsLayout,
      traffic: getAtlasV2TrafficsPageLayout,
      trafficV2: getAtlasTrafficsV2PageLayout,
      prices: getAtlasV2PricePageLayout,
      pricesV2: getAtlasPriceV2PageLayout,
      content: getAtlasV2ContentPageLayout,
      contentV2: getAtlasContentV2PageLayout,
      rating: getAtlasV2RatingPageLayout,
      ratingV2: getAtlasRatingsV2PageLayout,
      sales: getAtlasV2SalesPageLayout,
      salesV2: getAtlasSalesV2PageLayout,
      conversion: getAtlasV2ConversionLayout,
      conversionV2: getAtlasConversionV2PageLayout
    }[name] || getAtlasV2KeyMetricsLayout
  );
};

const getLayoutName = (pageType, tab, metricType) => {
  // summary page
  if (pageType === 'summaryPage') {
    return pageType;
  }

  // Temp routes for key metrics v2 <- will replace keymetrics when released
  if (['scorecardV2'].includes(tab) && ['all', 'microtrends'].includes(metricType)) {
    return 'keyMetricsV2';
  }

  // main page
  if (['keymetrics', 'all'].includes(metricType)) {
    return 'keymetrics';
  }

  return tab;
};

const shouldEnableExport = (metricType, user, flattenedWidgets) => {
  const lastWidgetView = _get(_last(flattenedWidgets), 'view');
  const failConditions = [!user.config.exportEnabled, _isEmpty(flattenedWidgets), !lastWidgetView];

  if (failConditions.find(_identity)) {
    return false;
  }

  if (['reviewsGrid', 'weeklyMetricsGridContainer', 'promotionsGrid', 'entityGrid'].includes(lastWidgetView.name)) {
    return true;
  }

  return _get(lastWidgetView.gridOptions, 'enableExport', false);
};

function getLayoutForEntity({
  app,
  retailer,
  user,
  tab,
  metricType,
  entity,
  pageType,
  filters,
  entityType,
  selectedEntityName
}) {
  const { queryParams } = app;

  const layoutName = getLayoutName(pageType, tab, metricType);

  const getPageLayout = getPageLayoutGetter(layoutName);

  const pageLayout = getPageLayout({
    app,
    retailer,
    user,
    tab,
    metricType,
    entity,
    filters,
    entityType,
    selectedEntityName
  });

  const { widgets, enableComparison, CustomPageContainer, dataConfig, ...rest } = pageLayout;

  const flattenedWidgets = flattenWidgets(widgets);
  const enableExport = shouldEnableExport(metricType, user, flattenedWidgets);

  const enableContentDownload =
    queryParams.tab === 'content' && ['contentAccuracy'].includes(queryParams.subtab) && enableExport;

  if (enableComparison || enableExport || enableContentDownload) {
    const subMenuItems = [];
    if (enableExport) {
      subMenuItems.push({
        icon: ExportIcon,
        text: enableContentDownload ? 'Download Content Accuracy' : 'Add to Download List',
        eventName: 'downloadEntityGrid'
      });
    }

    if (enableContentDownload) {
      subMenuItems.push({
        icon: InventoryIcon,
        text: 'Download Master Content',
        eventName: 'downloadContentCombine'
      });

      subMenuItems.push({
        icon: ContentCurrentIcon,
        text: 'Download Live Content',
        eventName: 'downloadCurrentContent'
      });
    }

    if (enableComparison) {
      subMenuItems.push({
        icon: CompareScaleIcon,
        text: 'Compare',
        eventName: 'showCompare'
      });
    }

    widgets.push({
      CustomComponent: PageContextMenu,
      name: 'pageContextMenu',
      view: {
        name: 'pageContextMenu',
        buttons: subMenuItems
      }
    });
  }

  // Don't add subtab navigation/header if we're on other pages
  if (layoutName !== 'summaryPage') {
    widgets.unshift({
      name: 'subtabNavigation',
      CustomComponent: AtlasV2SubtabNav,
      view: {}
    });
    widgets.unshift({
      name: 'subtabHeader',
      CustomComponent: SubtabHeader,
      view: {}
    });
  }

  return {
    CustomPageContainer,
    enableComparison,
    widgets,
    dataConfig,
    containerStyle: { marginTop: 60 },
    ...rest
  };
}

export default { getLayoutForEntity };
